import { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import { cn, formatPrice } from '../../lib/utils';

const SIZES = ['S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
const PRICE_RANGES = [
  { min: 0, max: 3000, label: 'Moins de 3 000 DA' },
  { min: 3000, max: 4000, label: '3 000 DA - 4 000 DA' },
  { min: 4000, max: 5000, label: '4 000 DA - 5 000 DA' },
  { min: 5000, max: 10000, label: 'Plus de 5 000 DA' },
];

export const ShopFilters = ({ 
  categories, 
  filters, 
  onFilterChange, 
  onClearFilters,
  isMobile = false,
  isOpen = true,
  onClose 
}) => {
  const [expandedSections, setExpandedSections] = useState({
    category: true,
    size: true,
    price: true,
    other: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleCategoryChange = (categoryId) => {
    onFilterChange('category', filters.category === categoryId ? null : categoryId);
  };

  const handleSizeChange = (size) => {
    onFilterChange('size', filters.size === size ? null : size);
  };

  const handlePriceChange = (range) => {
    if (filters.minPrice === range.min && filters.maxPrice === range.max) {
      onFilterChange('minPrice', null);
      onFilterChange('maxPrice', null);
    } else {
      onFilterChange('minPrice', range.min);
      onFilterChange('maxPrice', range.max);
    }
  };

  const hasActiveFilters = filters.category || filters.size || filters.minPrice || filters.isNew || filters.isBestseller || filters.isBridal;

  const FilterContent = () => (
    <>
      {/* Category */}
      <div className="border-b border-[#E0DFDD] pb-4 mb-4">
        <button
          onClick={() => toggleSection('category')}
          className="flex items-center justify-between w-full py-2"
        >
          <span className="text-xs uppercase tracking-widest font-medium">Catégorie</span>
          {expandedSections.category ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {expandedSections.category && (
          <div className="space-y-2 mt-3">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className={cn(
                  "block w-full text-left py-1 text-sm transition-colors",
                  filters.category === cat.id 
                    ? "text-[#D4AF37] font-medium" 
                    : "text-[#6B6A68] hover:text-[#1A1514]"
                )}
                data-testid={`filter-category-${cat.id}`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Size */}
      <div className="border-b border-[#E0DFDD] pb-4 mb-4">
        <button
          onClick={() => toggleSection('size')}
          className="flex items-center justify-between w-full py-2"
        >
          <span className="text-xs uppercase tracking-widest font-medium">Taille</span>
          {expandedSections.size ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {expandedSections.size && (
          <div className="flex flex-wrap gap-2 mt-3">
            {SIZES.map((size) => (
              <button
                key={size}
                onClick={() => handleSizeChange(size)}
                className={cn(
                  "size-option",
                  filters.size === size && "selected"
                )}
                data-testid={`filter-size-${size}`}
              >
                {size}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Price */}
      <div className="border-b border-[#E0DFDD] pb-4 mb-4">
        <button
          onClick={() => toggleSection('price')}
          className="flex items-center justify-between w-full py-2"
        >
          <span className="text-xs uppercase tracking-widest font-medium">Prix</span>
          {expandedSections.price ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {expandedSections.price && (
          <div className="space-y-2 mt-3">
            {PRICE_RANGES.map((range, idx) => (
              <button
                key={idx}
                onClick={() => handlePriceChange(range)}
                className={cn(
                  "block w-full text-left py-1 text-sm transition-colors",
                  filters.minPrice === range.min && filters.maxPrice === range.max
                    ? "text-[#D4AF37] font-medium" 
                    : "text-[#6B6A68] hover:text-[#1A1514]"
                )}
                data-testid={`filter-price-${idx}`}
              >
                {range.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Other Filters */}
      <div className="pb-4">
        <button
          onClick={() => toggleSection('other')}
          className="flex items-center justify-between w-full py-2"
        >
          <span className="text-xs uppercase tracking-widest font-medium">Autres</span>
          {expandedSections.other ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {expandedSections.other && (
          <div className="space-y-3 mt-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.isNew || false}
                onChange={(e) => onFilterChange('isNew', e.target.checked ? true : null)}
                className="w-4 h-4 accent-[#1A1514]"
                data-testid="filter-new"
              />
              <span className="text-sm text-[#6B6A68]">Nouveautés</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.isBestseller || false}
                onChange={(e) => onFilterChange('isBestseller', e.target.checked ? true : null)}
                className="w-4 h-4 accent-[#1A1514]"
                data-testid="filter-bestseller"
              />
              <span className="text-sm text-[#6B6A68]">Meilleures ventes</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.isBridal || false}
                onChange={(e) => onFilterChange('isBridal', e.target.checked ? true : null)}
                className="w-4 h-4 accent-[#1A1514]"
                data-testid="filter-bridal"
              />
              <span className="text-sm text-[#6B6A68]">Collection Mariée</span>
            </label>
          </div>
        )}
      </div>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <button
          onClick={onClearFilters}
          className="text-sm text-[#C87979] hover:underline mt-4"
          data-testid="clear-filters-btn"
        >
          Effacer les filtres
        </button>
      )}
    </>
  );

  if (isMobile) {
    if (!isOpen) return null;

    return (
      <>
        <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
        <div className="fixed top-0 left-0 bottom-0 w-80 bg-[#FCFBF9] z-50 p-6 overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-serif text-xl">Filtres</h3>
            <button onClick={onClose} className="p-2 hover:bg-[#F4E3E1] rounded-full">
              <X size={20} />
            </button>
          </div>
          <FilterContent />
        </div>
      </>
    );
  }

  return (
    <aside className="filter-sidebar" data-testid="shop-filters">
      <h3 className="font-serif text-xl mb-6">Filtres</h3>
      <FilterContent />
    </aside>
  );
};
